import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { Container, Row, Col, Card, Form, Table, Alert, Button } from 'react-bootstrap';
import { AppContext } from "lib/contexts/app-context";
import { useState, useRef, useEffect, useContext } from "react";
import SignatureCanvas from "react-signature-canvas";
import usePatients from "lib/hooks/usePatients";
import { useNavigate, useLocation } from "react-router-dom";
import { isPatientUser } from "lib/utils/util";

const InfraredConsentFormPage = () => {

  const { appState } = useContext(AppContext);

  const breadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.Dashboard : PAGE_ROUTES.ADMIN.Dashboard,
    },
    {
      label: isPatientUser(appState?.userRole) ? "Consent Forms" : "Patients Management",
      path: isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.ConsentForm : PAGE_ROUTES.ADMIN.PatientsManagement,
    },
    {
      label: "Infrared Consent Form",
      path: ''
    },
  ] as BreadcrumbItem[];

  const navigateTo = useNavigate();
    
  const { getPatientDetails, submitConsentForm, loading: patientsLoading } = usePatients();
  const sigPadRef = useRef<SignatureCanvas>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  const location = useLocation();
  const { patientId } = location.state || {};
  const [patientDetail, setPatientDetails] = useState<PatientDetails>();

  const [formData, setFormData] = useState({
      name: "",
      dob: "",
      email: "",
      phone: "",
      date: "",
      pregnant: "",
      medications: "",
      sweatingCondition: "",
      unstableAngina: "",
      recentHeartAttack: "",
      peripheralArteryDisease: "",
      whichMedicalCondition: "",
      medicalCondition: "",
      medicalProvider: "",
      signature: "", 
      patientId: patientId,
      therapyName: 'infrared'
  });

  useEffect(() => {
      if (!patientId) return;
      // Fetch patient details
      getPatientDetails(patientId, (res: any) => {
          setPatientDetails(res);
      });
  }, [patientId]);

  useEffect(() => {
    if (patientDetail) {
      setFormData((prev) => ({
        ...prev,
        name: patientDetail.name || '',
        email: patientDetail.email || '',
        phone: patientDetail.phone || '',
      }));
    }
  }, [patientDetail]);

  // Validation logic
  const validateForm = () => {
    let newErrors: any = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.pregnant) newErrors.pregnant = "Select Yes or No";
    if (!formData.medications) newErrors.medications = "Select Yes or No";
    if (!formData.sweatingCondition)
      newErrors.sweatingCondition = "Select Yes or No";
    if (!formData.unstableAngina)
      newErrors.unstableAngina = "Select Yes or No";
    if (!formData.recentHeartAttack)
      newErrors.recentHeartAttack = "Select Yes or No";
    if (!formData.peripheralArteryDisease)
      newErrors.peripheralArteryDisease = "Select Yes or No";
    if (!formData.medicalCondition)
      newErrors.medicalCondition = "Select Yes or No";
    if (!formData.medicalProvider)
      newErrors.medicalProvider = "Select Yes or No";
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.signature) newErrors.signature = "Signature is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSignature = () => {
      if (!sigPadRef.current?.isEmpty()) {
          const dataURL = sigPadRef.current!.toDataURL("image/png");
          setFormData(prev => ({ ...prev, signature: dataURL }));
          setShowSignaturePad(false); // Hide pad after saving
      } else {
          alert("Please provide a signature before saving.");
      }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const clearSignature = () => {
      sigPadRef.current?.clear();
      setFormData((prev) => ({ ...prev, signature: "" }));
  };

  const handleSubmit = async (e: any) => {
      e.preventDefault();

      if (!validateForm()) {
        // alert("Please fill all required fields.");
        return;
      }

      if (submitting) return; // Prevent double click
      setSubmitting(true); // Start loading

        // Check if signature EXISTS in formData (not on canvas)
      if (!formData.signature) {
          alert("Please provide a signature.");
          setSubmitting(false);
          return;
      }

      // Now package everything
      const payload = {
      ...formData,
      patientId: patientId,
      };
      try {
        submitConsentForm(formData, (res) => {
          if (isPatientUser(appState?.userRole)) {
            navigateTo(PAGE_ROUTES.PATIENT.ConsentForm);
          } else {
            navigateTo(PAGE_ROUTES.ADMIN.PatientProfile, {
              state: { patientId: patientId, patientName: patientDetail.name }
            });
          }
          // navigateTo(isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.ConsentForm : PAGE_ROUTES.ADMIN.PatientProfile);
        });
      } catch (error) {
        console.error("Error submitting:", error);
      } finally {
        setSubmitting(false); // Stop loading after response
      }

  };

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>

      <Container className="my-4 p-0">
        <Card className="shadow-sm">
          <Card.Header className="bg-dark text-white">
            <h2 className="mb-0 text-center">INFRARED SAUNA CONSENT FORM</h2>
          </Card.Header>

          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Client Name:</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                  />
                  {errors.name && <div className="text-danger small">{errors.name}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Date of Birth:</strong></Form.Label>
                  <Form.Control
                    type="date"
                    name="dob"
                    value={formData.dob || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.dob}
                  />
                  {errors.dob && <div className="text-danger small">{errors.dob}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Email:</strong></Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email address"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                  />
                  {errors.email && <div className="text-danger small">{errors.email}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Phone:</strong></Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter phone number"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.phone}
                  />
                  {errors.phone && <div className="text-danger small">{errors.phone}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="info" className="mb-4">
              <strong>Important Information:</strong> To provide the best experience for our guests,
              sauna use is by appointment only. Please call or stop by our front desk to schedule
              a time for your session. Permission to use the 3 in 1* infrared sauna is contingent
              on guests providing accurate answers to the questions below so that we can mutually
              agree on the health and well-being of the sauna user, as well as signing this agreement.
            </Alert>

            <div className="mb-4">
              <h5 className="mb-3"><strong>PLEASE ANSWER THE FOLLOWING QUESTIONS:</strong></h5>

              <Table bordered responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th >Questions</th>
                    <th className="text-center">Yes</th>
                    <th className="text-center">No</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>Are you pregnant? How far along?</strong>
                      {errors.pregnant && <div className="text-danger small">{errors.pregnant}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="pregnant"
                        value="yes"
                        checked={formData.pregnant === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.pregnant}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="pregnant"
                        value="no"
                        checked={formData.pregnant === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.pregnant}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Are you taking any medications?</strong>
                      {errors.medications && <div className="text-danger small">{errors.medications}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medications"
                        value="yes"
                        checked={formData.medications === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.medications}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medications"
                        value="no"
                        checked={formData.medications === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.medications}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Have you been diagnosed with any medical condition, such as anhidrosis, that may limit or prevent your ability to sweat?</strong>
                      {errors.sweatingCondition && <div className="text-danger small">{errors.sweatingCondition}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="sweatingCondition"
                        value="yes"
                        checked={formData.sweatingCondition === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.sweatingCondition}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="sweatingCondition"
                        value="no"
                        checked={formData.sweatingCondition === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.sweatingCondition}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Do you have unstable angina?</strong>
                      {errors.unstableAngina && <div className="text-danger small">{errors.unstableAngina}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="unstableAngina"
                        value="yes"
                        checked={formData.unstableAngina === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.unstableAngina}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="unstableAngina"
                        value="no"
                        checked={formData.unstableAngina === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.unstableAngina}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Have you had a recent heart attack?</strong>
                      {errors.recentHeartAttack && <div className="text-danger small">{errors.recentHeartAttack}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="recentHeartAttack"
                        value="yes"
                        checked={formData.recentHeartAttack === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.recentHeartAttack}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="recentHeartAttack"
                        value="no"
                        checked={formData.recentHeartAttack === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.recentHeartAttack}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Do you have Peripheral Artery Disease?</strong>
                      {errors.peripheralArteryDisease && <div className="text-danger small">{errors.peripheralArteryDisease}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="peripheralArteryDisease"
                        value="yes"
                        checked={formData.peripheralArteryDisease === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.peripheralArteryDisease}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="peripheralArteryDisease"
                        value="no"
                        checked={formData.peripheralArteryDisease === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.peripheralArteryDisease}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Have you been diagnosed with any other medical condition? If "yes," which condition?</strong>
                      <Form.Control
                        type="text"
                        size="sm"
                        className="mt-2"
                        placeholder="Which condition?"
                        name="whichMedicalCondition"
                        value={formData.whichMedicalCondition || ''}
                        onChange={handleChange}
                      />
                      {errors.medicalCondition && <div className="text-danger small">{errors.medicalCondition}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medicalCondition"
                        value="yes"
                        checked={formData.medicalCondition === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.medicalCondition}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medicalCondition"
                        value="no"
                        checked={formData.medicalCondition === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.medicalCondition}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>If you answered “yes” to any of the above questions; have you consulted with your medical
                        provider about using a 3 in 1 infrared sauna?</strong>
                      {errors.medicalProvider && <div className="text-danger small">{errors.medicalProvider}</div>}
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medicalProvider"
                        value="yes"
                        checked={formData.medicalProvider === "yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.medicalProvider}
                      />
                    </td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="radio"
                        name="medicalProvider"
                        value="no"
                        checked={formData.medicalProvider === "no"}
                        onChange={handleChange}
                        isInvalid={!!errors.medicalProvider}
                      />
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <Alert variant="success">
              <p className="mb-0">
                It is always important to maintain proper hydration levels during far infrared therapy.
                Dehydration will increase carbohydrate utilization and cause less fat to be burned for energy.
                We highly recommend drinking a minimum of 8 oz. of water prior to entering the sauna and a
                minimum of 8 oz. of water after sauna use.
              </p>
            </Alert>

            <h5 className="mb-3"><strong>INFRARED SAUNA AGREEMENT/ACKNOWLEDGEMENT</strong></h5>
            <Alert variant="light" className="border">
              <ol className="mb-0">
                <li className="mb-3">
                  The use of drugs, medication or alcohol prior to or during the 3 in 1 infrared sauna session may lead to dizziness or unconsciousness. Clients using any medications must consult a physician or pharmacist prior to the use of the sauna.
                </li>
                <li className="mb-3">
                  Please consult your physician if you are in doubt of your ability to use the 3 in 1 infrared sauna for health reasons.
                </li>
                <li className="mb-3">
                  No one under the age of 18 is permitted in the 3 in 1 infrared sauna unless accompanied by a supervising adult.
                </li>
                <li className="mb-3">
                  Discontinue the use of the sauna if you feel light-headed, dizzy, or heat exhausted.
                </li>
                <li className="mb-3">
                  Sauna sessions should be limited to no more than 45 minutes and temperatures must stay below 150 degrees Fahrenheit.
                </li>
                <li className="mb-3">
                  Plastic water bottles are not permitted in the sauna.
                </li>
                <li className="mb-3">
                  Pregnant women should consult their physician prior to the use of the sauna. Excessive body temperatures have a potential for causing fetal damage during the early days of pregnancy.
                </li>
              </ol>
            </Alert>


            <div className="mt-4">
               <h5 className="mb-3"><strong>INFORMED CONSENT & LIABILITY WAIVER</strong></h5>
              <p className="mb-4">
                I confirm that I have read and understood the information above, fully disclosed my medical history,
                had the opportunity to ask questions, and understand the potential risks. I voluntarily consent to
                undergo Infrared Sauna Therapy and release Heal Wellness Clinic, its practitioners, and staff from
                liability for any adverse effects, provided all safety protocols are followed.
              </p>

              <Row className="mt-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <div>
                      <Form.Label className="me-2"><strong>SIGNATURE</strong></Form.Label>

                      {!formData.signature && !showSignaturePad && (
                      <Button type="button" onClick={() => setShowSignaturePad(true)}>
                          Add Signature
                      </Button>
                      )}

                      {showSignaturePad && (
                      <div style={{ border: "1px solid #000", padding: "10px" }}>
                          <SignatureCanvas
                          ref={sigPadRef}
                          penColor="black"
                          canvasProps={{ width: 400, height: 200, className: "sigCanvas" }}
                          />
                          <div className="mt-2">
                          <Button type="button" onClick={handleSaveSignature} className="me-2">Save</Button>
                          <Button type="button" onClick={() => sigPadRef.current?.clear()}>
                              Clear
                          </Button>
                          </div>
                      </div>
                      )}

                      {formData.signature && !showSignaturePad && (
                      <div className="d-flex align-items-center gap-3 mt-2">
                          <img src={formData.signature} alt="Signature" width="200" height="80" style={{ border: "1px solid #ccc" }} />
                          <Button type="button" onClick={() => setShowSignaturePad(true)}>Edit</Button>
                      </div>
                      )}
                    </div>
                    {errors.signature && <div className="text-danger small">{errors.signature}</div>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>DATE</strong></Form.Label>
                    <Form.Control
                      type="date"
                      name="date"
                      value={formData.date || ''}
                      onChange={handleChange}
                      className="border-top-0 border-start-0 border-end-0 rounded-0"
                      isInvalid={!!errors.date}
                    />
                    {errors.date && <div className="text-danger small">{errors.date}</div>}
                  </Form.Group>
                </Col>
              </Row>
            </div>
              <div className="d-flex align-items-center gap-3 mt-2">
                <Button 
                  className="mt-1" 
                  type="submit" 
                  disabled={submitting} // <-- Disable during API call
                >
                  {submitting ? "Submitting..." : "Submit"}  {/* Show loading text */}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </DashboardLayout>
  );
};

export default InfraredConsentFormPage;